import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TenantApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { transformPersonalizationRequest } from "@/lib/utils/personalization";
import { savePersonalizationToStorage } from "@/lib/utils/personalization-storage";
import { createOpenApiConfiguration } from "@/lib/utils";
import type { ColorPaletteId } from "@/lib/theme/color-palettes";
import type { DashboardAppearance } from "@/lib/theme/dashboard-appearance";
import type { ThemeModeControl, FontSize } from "@/components/dashboard/DashboardThemeProvider";

export const useTenantPersonalizationUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (request: {
      tenantId: number;
      colorPalette: ColorPaletteId;
      customColor: string | null;
      grainEnabled: boolean;
      gradientEnabled: boolean | null;
      themeModeControl: ThemeModeControl | null;
      fontSize: FontSize | null;
      dashboardAppearance: DashboardAppearance;
    }) => {
      const service = new TenantApi(createOpenApiConfiguration());

      const updateRequest = transformPersonalizationRequest(
        request.colorPalette,
        request.customColor,
        request.grainEnabled,
        request.gradientEnabled,
        request.themeModeControl,
        request.fontSize,
        request.dashboardAppearance,
      );

      const response = await service.apiAccountTenantIdPersonalizationPut({
        tenantId: request.tenantId,
        updatePersonalizationRequest: updateRequest,
      });

      return response;
    },
    retry: 1,
    onSuccess: async (_, variables) => {
      const saved = {
        colorPalette: variables.colorPalette,
        customColor: variables.customColor,
        grainEnabled: variables.grainEnabled,
        gradientEnabled: variables.gradientEnabled ?? false,
        themeModeControl: variables.themeModeControl ?? "user-choice",
        fontSize: variables.fontSize ?? "medium",
        dashboardAppearance: variables.dashboardAppearance,
      };

      queryClient.setQueryData(
        [QueryKeys.TenantPersonalization, variables.tenantId],
        saved,
      );

      savePersonalizationToStorage(variables.tenantId, saved);

      toast({
        title: "Personalization updated",
        description: "Personalization settings updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update personalization settings";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
